import { useEffect, useState } from 'react';
import styles from '../bills.module.scss';
import DataTable, { ColumnType } from '../../../../common/components/xepp/table/DataTable';
import Button from '../../../../common/components/xepp/button/Button';
import { EyeIcon, FilterIcon2, PostpaidActiveIcon } from '../../../../assets/icons';
import SearchInput from '../../../../common/components/xepp/search-input/SearchInput';
import { Transaction, TransactionChannelMap, TransactionStatusMap } from '../../../../model/transaction';
import EmptyState from '../../../../common/components/xepp/empty-state/EmptyState';
import CommonSideDialog from '../../../../common/components/side-dialog/CommonSideDialog';
import TransactionDetails from '../../../../common/components/xepp/transaction-details/TransactionDetails';
import { Utility } from '../../../../model/bill-settings/bill-settings';
import { useLazyGetPostpaidTransactionsQuery } from '../../../../service/transaction-service';
import { formatCurrency, formatTimestamp } from '../../../../utils';
import { TablePagination } from '../../../../common/components/xepp/table-pagination/TablePagination';
import { FilterItem } from '../../../../common/components/xepp/table-filter/table-filter.type';
import Filter from '../../../../common/components/xepp/table-filter/table-filter-components';
import { useDebounce } from '../../../../hooks/useDebounce';
import { getErrorMessage } from '../../../../utils/error-handler';

interface PostPaidHistoryProps {
  selectedUtility: Utility;
}
export const PostpaidHistory = ({ selectedUtility }: PostPaidHistoryProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction>();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<string>('');
  const [selectedBillingStatus, setSelectedBillingStatus] = useState<boolean | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<{
    PaymentStatus: string;
    BillingStatus?: boolean | null;
    startDate?: Date | null;
    endDate?: Date | null;
  }>({
    PaymentStatus: '',
    BillingStatus: null,
    startDate: null,
    endDate: null
  });
  const [getPostPaidTransactions, { data, isLoading, isFetching }] = useLazyGetPostpaidTransactionsQuery();
  const [Page, setPage] = useState(1);
  const PageSize = 15

  const { count } = data ?? {};

  useEffect(() => {
    if (selectedUtility) {
      const filterParams = {
        Page,
        PageSize,
        UtilityId: selectedUtility.id,
        ...(appliedFilters.PaymentStatus && { PaymentStatus: appliedFilters.PaymentStatus }),
        ...(appliedFilters.BillingStatus !== null && { BillingStatus: appliedFilters.BillingStatus }),
        ...(appliedFilters.startDate && { startDate: appliedFilters.startDate }),
        ...(appliedFilters.endDate && { endDate: appliedFilters.endDate }),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm })
      };

      getPostPaidTransactions(filterParams).unwrap()
        .catch(err => getErrorMessage(err?.data))
    }
  }, [selectedUtility, Page, PageSize, appliedFilters, debouncedSearchTerm, getPostPaidTransactions]);

  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm && searchTerm !== '') {
      setPage(1);
    }
  }, [searchTerm, debouncedSearchTerm]);


  const handleOpenFilter = () => {
    setIsFilterOpen(true);
  };

  const handleCloseFilter = () => {
    setIsFilterOpen(false);
  };

  const handleApplyFilter = () => {
    setAppliedFilters({
      PaymentStatus: selectedPaymentStatus,
      BillingStatus: selectedBillingStatus,
      startDate,
      endDate
    });

    setIsFilterOpen(false);
    setPage(1);
  };

  const handleResetFilter = () => {
    setSelectedPaymentStatus('');
    setSelectedBillingStatus(null);
    setStartDate(null);
    setEndDate(null);
    setIsFilterOpen(false);

    setAppliedFilters({
      PaymentStatus: '',
      BillingStatus: null,
      startDate: null,
      endDate: null
    });

    setPage(1);
  };

  const handlePaymentStatusChange = (value: string) => {
    setSelectedPaymentStatus(value);
  };

  const handleBillingStatusChange = (value: string) => {
    if (value === 'notified') {
      setSelectedBillingStatus(true);
    } else if (value === 'not-notified') {
      setSelectedBillingStatus(false);
    } else {
      setSelectedBillingStatus(null);
    }
  };

  const getBillingStatusValue = () => {
    if (selectedBillingStatus === true) return 'notified';
    if (selectedBillingStatus === false) return 'not-notified';
    return '';
  };


  const filterItems: FilterItem[] = [
    {
      id: 'payment_status',
      label: 'PAYMENT STATUS',
      type: 'radio',
      options: [
        {
          id: 'In-Progress',
          label: 'In-Progress',
          value: 'In-Progress',
          isSelected: selectedPaymentStatus === 'IN-Progress'
        },
        {
          id: 'failed',
          label: 'Failed',
          value: 'Failed',
          isSelected: selectedPaymentStatus === 'Failed'
        },
        {
          id: 'completed',
          label: 'Completed',
          value: 'Completed',
          isSelected: selectedPaymentStatus === 'Completed'
        }
      ],
      onChange: handlePaymentStatusChange
    },
    {
      id: 'billing_status',
      label: 'BILLING STATUS',
      type: 'radio',
      options: [
        {
          id: 'notified',
          label: 'Notified',
          value: 'notified',
          isSelected: getBillingStatusValue() === 'notified'
        },
        {
          id: 'not-notified',
          label: 'Not Notified',
          value: 'not-notified',
          isSelected: getBillingStatusValue() === 'not-notified'
        }
      ],
      onChange: handleBillingStatusChange
    },
  ];



  const handleViewTransaction = (transaction: Transaction) => {
    setIsOpen(true);
    setSelectedTransaction(transaction)
  };

  const columns: Array<ColumnType<Transaction>> = [
    {
      accessor: 'billingDetails.customerName',
      header: 'Name of Customer',
      render: (record: Transaction) => (<span>
        {record.user?.firstName} {record.user.lastName}
      </span>)
    },
    {
      accessor: 'reference',
      header: 'Reference',
      render: (record: Transaction) => (<span title={record.reference} className={styles.ellipsisText}>
        {record.reference}
      </span>)
    },
    {
      accessor: 'billingDetails.amount.amount',
      header: 'Amount Paid',
      render: (record: Transaction) => (
        <span>
          {record.billingDetails?.amount ? formatCurrency(record.billingDetails?.amount.amount, { currency: record.billingDetails?.amount.currency }) : "N/A"}
        </span>
      ),
    },
    {
      accessor: 'type',
      header: 'Channel',
      render: (record: Transaction) => (
        <span className={styles.channelBadge}>
          {TransactionChannelMap[record.type]}
        </span>
      ),
    },
    {
      accessor: 'status',
      header: 'Payment Status',
      render: (record: Transaction) => (
        <span
          className={`${styles.statusIndicator} ${record.status
              ? styles[TransactionStatusMap[record.status].toLowerCase()]
              : ''
            }`}
        >
          {TransactionStatusMap[record.status]}
        </span>
      ),
    },
    {
      accessor: 'billingDetails.postpaidStatus',
      header: 'Postpaid Status',
      render: (record: Transaction) => (
        <span
          className={`${styles.statusIndicator} ${record.billingDetails?.isBillingNotified
              ? styles[TransactionStatusMap[2].toLowerCase()]
              : styles[TransactionStatusMap[3].toLowerCase()]
            }`}
        >
          {record.billingDetails?.isBillingNotified ? "Notified" : "Not-Notified"}
        </span>
      ),
    },
    {
      header: 'Date & Time',
      accessor: 'createdAt',
      render: (record: Transaction) => (
        <span>
          {formatTimestamp(record.billingDetails?.createdAt ?? "")}
        </span>
      ),
    },
    {
      accessor: 'id',
      header: "",
      render: (record: Transaction) => (
        <Button
          leftIcon={<EyeIcon className={styles.icon} />}
          variant='text'
          text="View"
          onClick={() => handleViewTransaction(record)}
        />
      )
    }
  ];




  const headerActions = (
    <>
      <SearchInput
        initialValue={searchTerm}
        onSearch={(value: string) => setSearchTerm(value)}
        placeholder="Search by reference..."
      />
      <Button
        leftIcon={<FilterIcon2 className={styles.icon} />}
        text='Add Filter'
        variant='outline'
        type='button'
        onClick={handleOpenFilter}
      />
    </>
  );

  return (
    <div className={styles.historyContainer}>
      <Filter
        filterItems={filterItems}
        onApplyFilter={handleApplyFilter}
        onResetFilter={handleResetFilter}
        onClose={handleCloseFilter}
        isOpen={isFilterOpen}
      />
      {isOpen && <CommonSideDialog onCloseDialog={() => setIsOpen(!isOpen)}>
        <TransactionDetails
          transactionType='billing'
          transaction={{
            date: selectedTransaction?.billingDetails?.createdAt ?? "",
            time: selectedTransaction?.billingDetails?.createdAt ?? "",
            customerName: `${selectedTransaction?.user.firstName} ${selectedTransaction?.user.lastName}`,
            amount: selectedTransaction?.amount.amount
              ? formatCurrency(selectedTransaction?.amount.amount, { currency: selectedTransaction?.amount.currency })
              : "N/A",
            paymentStatus: selectedTransaction?.status ?? 0,
            transactionId: selectedTransaction?.externalReference ?? "",
            unitBought: "200",
            paymentChannel: selectedTransaction?.type ?? 0,
            token: selectedTransaction?.externalReference ?? "",
            billingStatus: selectedTransaction?.billingDetails?.isBillingNotified,
            reference: selectedTransaction?.reference ?? "",
                                }}
        onRevend={() => { }}
        onClose={() => setIsOpen(!isOpen)}
                       />
      </CommonSideDialog>}
      <DataTable<Transaction>
        data={data?.items ?? []}
        columns={columns}
        loading={isLoading}
        isUpdating={isFetching}
        headerActions={headerActions}
        emptyComponent={<EmptyState icon={<PostpaidActiveIcon />} title='No post paid history data' />}
      />
      <TablePagination isLoading={isFetching} totalItems={count ?? 0} itemsPerPage={PageSize} currentPage={Page} onPageChange={setPage} />
    </div>
  );
}
